import { gql, useMutation, useQuery } from '@apollo/client'
import SendIcon from '@mui/icons-material/Send'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import React, { useEffect, useRef, useState } from 'react'
import { User } from '../../../schema'

const createCommentMutation = gql`
  mutation CreateAlertComment($input: CreateAlertCommentInput!) {
    createAlertComment(input: $input) {
      id
    }
  }
`

const userSearchQuery = gql`
  query UserSearch($search: String) {
    users(input: { search: $search, first: 10 }) {
      nodes {
        id
        name
        email
      }
    }
  }
`

interface AlertCommentInputProps {
  alertID: number
  onCommentAdded: () => void
}

interface MentionMap {
  [name: string]: string // name -> id
}

export default function AlertCommentInput({
  alertID,
  onCommentAdded,
}: AlertCommentInputProps): React.JSX.Element {
  const [message, setMessage] = useState('')
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [mentionMap, setMentionMap] = useState<MentionMap>({})
  const inputRef = useRef<HTMLDivElement>(null)

  const [createComment, { loading: creating }] = useMutation(
    createCommentMutation,
    {
      onCompleted: () => {
        setMessage('')
        setMentionMap({})
        onCommentAdded()
      },
      onError: (error) => {
        console.error('Failed to create comment:', error)
      },
      refetchQueries: ['AlertCommentsQuery'],
      awaitRefetchQueries: true,
    },
  )

  // Determine if we should search for users
  useEffect(() => {
    const cursor =
      inputRef.current?.querySelector('textarea')?.selectionStart || 0
    const textBeforeCursor = message.slice(0, cursor)
    const match = textBeforeCursor.match(/@(\w+)$/)

    if (match && match[1].length > 0) {
      setMentionQuery(match[1])
      setAnchorEl(inputRef.current)
    } else {
      setMentionQuery(null)
      setAnchorEl(null)
    }
  }, [message])

  const { data: userData, loading: searching } = useQuery(userSearchQuery, {
    variables: { search: mentionQuery },
    skip: mentionQuery === null || mentionQuery.length === 0,
  })

  const handleSelectUser = (user: { id: string; name: string }) => {
    const cursor =
      inputRef.current?.querySelector('textarea')?.selectionStart || 0
    const textBeforeCursor = message.slice(0, cursor)
    const textAfterCursor = message.slice(cursor)

    const match = textBeforeCursor.match(/@(\w*)$/)
    if (match) {
      const prefix = textBeforeCursor.slice(0, match.index)
      // Insert friendly name only: @Name
      const newText = prefix + `@${user.name} ` + textAfterCursor
      setMessage(newText)

      // Track ID for transformation later
      setMentionMap((prev) => ({
        ...prev,
        [user.name]: user.id,
      }))
    }

    setMentionQuery(null)
    setAnchorEl(null)
    inputRef.current?.querySelector('textarea')?.focus()
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!message.trim()) return

    // Transform friendly mentions to Markdown links for storage
    // Replace @Name with [@Name](/users/id) if name exists in map
    let finalMessage = message
    const usedIDs = new Set<string>()

    Object.entries(mentionMap).forEach(([name, id]) => {
      const regex = new RegExp(`@${name}\\b`, 'g')
      if (regex.test(finalMessage)) {
        finalMessage = finalMessage.replace(regex, `[@${name}](/users/${id})`)
        usedIDs.add(id)
      }
    })

    createComment({
      variables: {
        input: {
          alertID,
          message: finalMessage,
          mentionedUserIDs: Array.from(usedIDs),
        },
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow newlines with Shift+Enter, submit with Enter
    if (e.key === 'Enter' && !e.shiftKey && !anchorEl) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Box>
      <TextField
        ref={inputRef}
        fullWidth
        multiline
        maxRows={4}
        variant='outlined'
        placeholder='Write a comment...'
        label='Comment'
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <IconButton
              color='primary'
              disabled={creating || !message.trim()}
              onClick={() => handleSubmit()}
              edge='end'
              size='small'
              sx={{ alignSelf: 'flex-end', mb: 0.5 }}
            >
              {creating ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          ),
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            style: { maxHeight: 300, width: 250 },
          },
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        autoFocus={false}
      >
        {searching && <MenuItem disabled>Loading...</MenuItem>}
        {userData?.users?.nodes?.map((user: User) => (
          <MenuItem key={user.id} onClick={() => handleSelectUser(user)}>
            {user.name} {user.email && `(${user.email})`}
          </MenuItem>
        ))}
        {!searching && userData?.users?.nodes?.length === 0 && (
          <MenuItem disabled>No users found</MenuItem>
        )}
      </Menu>
    </Box>
  )
}
